import classNames from "classnames"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import { useEffect } from "react"
import { Col, Row } from "react-bootstrap"
import Form from "react-bootstrap/Form"
import "./WysingField.css"

export type WysiwygProps = {
  layout?: "vertical" | "horizontal"
  labelColSize?: number
  inputColSize?: number
  label?: string
  helperText?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  readonly?: boolean
  disabled?: boolean
  isValid?: boolean
  successMessage?: string
  errorMessage?: string
  size?: "sm" | "md" | "lg"
  rows?: number
  resize?: "none" | "both" | "horizontal" | "vertical" | "auto"
  required?: boolean
  className?: string
  style?: React.CSSProperties
}

export function WysiwygField({
  layout = "vertical",
  label,
  helperText,
  placeholder,
  labelColSize,
  inputColSize,
  value,
  onChange,
  readonly,
  disabled,
  isValid,
  successMessage,
  errorMessage,
  rows = undefined,
  required = false,
  className,
  style,
}: WysiwygProps) {
const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3],
      },
    }),
    Link.configure({
      openOnClick: false,
    }),
  ],
  content: value || "",
  editable: !readonly && !disabled,
  onUpdate: ({ editor }) => {
    const html = editor.getHTML()
    // Return empty string if editor is empty
    const isEmpty = editor.isEmpty
    onChange?.(isEmpty ? '' : html)
  },
})

useEffect(() => {
  if (editor && value !== undefined) {
    const currentHTML = editor.getHTML()
    const isEmpty = editor.isEmpty
    
    // Treat empty paragraph and empty string as the same
    if ((isEmpty && (value === '' || value === '<p></p>')) || value === currentHTML) {
      return
    }
    
    editor.commands.setContent(value || "")
  }
}, [value, editor])


  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "")
    }
  }, [value, editor])

  const requiredMarker = required ? <span className="text-danger">*</span> : undefined
  const labelCmp = label ? (
    <Form.Label className="mb-0">
      {label}
      {requiredMarker}
    </Form.Label>
  ) : undefined
  const helperTextCmp = helperText ? <Form.Text className="text-muted mt-0">{helperText}</Form.Text> : undefined
  
  const controlCmp = (
    <div
      className={classNames("tiptap-editor-wrapper", {
        "is-invalid": isValid === false,
        "is-valid": isValid === true,
        "disabled": disabled,
      })}
      style={{ backgroundColor: "white" }}
    >
      {editor && (
        <div className="tiptap-toolbar">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive("heading", { level: 1 }) ? "is-active" : ""}
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive("heading", { level: 2 }) ? "is-active" : ""}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive("heading", { level: 3 }) ? "is-active" : ""}
          >
            H3
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? "is-active" : ""}
          >
            <strong>Bold</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? "is-active" : ""}
          >
            <em>Italic</em>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleUnderline().run()
            }}
            className={editor.isActive("underline") ? "is-active" : ""}
          >
            Underline
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt("Enter URL:")
              if (url) {
                editor.chain().focus().setLink({ href: url }).run()
              }
            }}
            className={editor.isActive("link") ? "is-active" : ""}
          >
            Link
          </button>
        </div>
      )}
      <EditorContent editor={editor} placeholder={placeholder} rows={rows}  />
    </div>
  )

  let semanticTextCmp = undefined
  
  if (errorMessage) {
    semanticTextCmp = <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
  } else if (successMessage) {
    semanticTextCmp = <Form.Control.Feedback type="valid">{successMessage}</Form.Control.Feedback>
  }

  if (layout === "horizontal") {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={labelColSize ?? 4} className="d-flex flex-column">
          {labelCmp}
          {helperTextCmp}
        </Col>
        <Col xs={inputColSize ?? 8} className="d-flex flex-column align-items-stretch">
          {controlCmp}
          {semanticTextCmp}
        </Col>
      </Form.Group>
    )
  } else {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={inputColSize ?? 12} className="d-flex flex-column mb-2">
          {labelCmp}
          {helperTextCmp}
          {controlCmp}
          {semanticTextCmp}
        </Col>
      </Form.Group>
    )
  }
}
